// First-touch acquisition cookie for signup attribution, written only with CookieYes analytics consent.
// Interface: docs/marketing/app-handoff-contract.md (owned by the app repo). Identical in www/ and blog/.
const COOKIE = 'myphoner_ft'
const MAX_AGE = 7776000
const UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
const CLICK_IDS = ['gclid', 'fbclid', 'msclkid', 'li_fat_id']
const CAPS = { host: 100, path: 300, ref: 100, utm: 150 }

// The contract's "encoded bytes": the JSON-escaped value, URI-encoded
const encodedSize = (value) => encodeURIComponent(JSON.stringify(value)).length

// Removes whole characters from the end until the value fits, so no escape or character is split
const cap = (value, max) => {
  // Every character encodes to at least one byte: start from at most max characters, so the loop stays short
  const chars = Array.from(value).slice(0, max)
  while (chars.length && encodedSize(chars.join('')) > max) chars.pop()
  return chars.join('')
}

const isMyphonerHost = (host) => host === 'myphoner.com' || host.endsWith('.myphoner.com')

const refHost = (referrer) => {
  if (!referrer) return null
  let host
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return null
  }
  if (!host || isMyphonerHost(host) || host === 'myphoner.eu') return null
  return cap(host, CAPS.ref)
}

const normalPath = (pathname) => {
  const p = (pathname || '/').toLowerCase()
  const last = p.split('/').pop()
  return p.endsWith('/') || last.includes('.') ? p : `${p}/`
}

const firstTouch = ({ href, referrer, now = new Date() }) => {
  const url = new URL(href)
  const payload = {
    v: 1,
    ts: now.toISOString().replace(/\.\d{3}Z$/, 'Z'),
    host: cap(url.hostname.toLowerCase(), CAPS.host),
    path: cap(normalPath(url.pathname), CAPS.path),
    ref: refHost(referrer),
  }
  for (const key of UTMS) {
    const value = (url.searchParams.get(key) || '').trim()
    if (value) payload[key] = cap(value, CAPS.utm)
  }
  const click = CLICK_IDS.find((id) => url.searchParams.has(id))
  if (click) payload.click = click
  return payload
}

// Browsers reject Domain=.myphoner.com on other hosts (deploy previews, localhost): host-only there
const cookieAttributes = (hostname) =>
  `; Max-Age=${MAX_AGE}; Path=/${isMyphonerHost(hostname) ? '; Domain=.myphoner.com' : ''}; SameSite=Lax; Secure`

const hasCookie = (doc) => doc.cookie.split(/;\s*/).some((c) => c.startsWith(`${COOKIE}=`))

// true or false once CookieYes can tell; null while it can't (not loaded, blocked, failing)
const analyticsConsent = (win) => {
  try {
    if (!win.getCkyConsent) return null
    return Boolean(win.getCkyConsent().categories.analytics)
  } catch {
    return null
  }
}

// Attribution must never break the page or the code that runs after it (the affiliate cookie)
const safely = (fn) => (...args) => {
  try {
    fn(...args)
  } catch {
    // no attribution for this visitor
  }
}

// Calls grant when CookieYes reports analytics consent (on load, banner load or a later change) and
// revoke when it reports none, including a withdrawal this page never saw as an event. Unknown: neither.
const whenAnalyticsConsent = (win, { grant, revoke }) => {
  const doc = win.document
  const onGrant = safely(grant)
  const onRevoke = safely(revoke)
  const apply = () => {
    const consent = analyticsConsent(win)
    if (consent === true) onGrant()
    if (consent === false) onRevoke()
  }
  apply()
  doc.addEventListener('cookieyes_banner_load', apply)
  doc.addEventListener('cookieyes_consent_update', (event) => {
    const detail = (event && event.detail) || {}
    const listed = (list) => Array.isArray(list) && list.includes('analytics')
    if (listed(detail.accepted)) onGrant()
    if (listed(detail.rejected)) onRevoke()
  })
}

const initAttribution = (win) => {
  const doc = win.document
  whenAnalyticsConsent(win, {
    grant: () => {
      if (hasCookie(doc)) return
      const payload = firstTouch({ href: win.location.href, referrer: doc.referrer })
      doc.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(payload))}${cookieAttributes(win.location.hostname)}`
    },
    revoke: () => {
      if (!hasCookie(doc)) return
      doc.cookie = `${COOKIE}=${cookieAttributes(win.location.hostname).replace(`Max-Age=${MAX_AGE}`, 'Max-Age=0')}`
    },
  })
}

module.exports = { COOKIE, UTMS, CAPS, firstTouch, cookieAttributes, encodedSize, initAttribution, whenAnalyticsConsent }
